<template>
  <a-modal
    :visible="value"
    width="80vw"
    :footer="null"
    :centered="true"
    :closable="false"
    :maskClosable="false"
    :maskStyle="{ background: '#1c1c1c' }"
    wrapClassName="c-turn-modal"
    @cancel="close"
  >
    <div
      class="c-select"
      :class="{ cn: isCN, 'lighting-sync': purpose === 'lighting-sync' }"
    >
      <div class="title">{{ title }}</div>
      <div class="options">
        <md-checkbox
          v-if="purpose !== 'axis-sync'"
          v-model="selected.led1"
          class="checkbox"
        >
          {{ $t("selectVersion.led") }} 1
        </md-checkbox>
        <md-checkbox
          v-if="purpose !== 'axis-sync'"
          v-model="selected.led2"
          class="checkbox"
        >
          {{ $t("selectVersion.led") }} 2
        </md-checkbox>
        <md-checkbox
          v-if="purpose !== 'axis-sync'"
          v-model="selected.led3"
          class="checkbox"
        >
          {{ $t("selectVersion.led") }} 3
        </md-checkbox>
        <md-checkbox
          v-if="
            purpose === 'export' ||
            purpose === 'axis-sync' ||
            purpose === 'lighting-sync'
          "
          v-model="selected.axis1"
          class="checkbox"
        >
          {{ $t("selectVersion.inSwitch") }} 1
        </md-checkbox>
        <md-checkbox
          v-if="
            purpose === 'export' ||
            purpose === 'axis-sync' ||
            purpose === 'lighting-sync'
          "
          v-model="selected.axis2"
          class="checkbox"
        >
          {{ $t("selectVersion.inSwitch") }} 2
        </md-checkbox>
        <md-checkbox
          v-if="
            purpose === 'export' ||
            purpose === 'axis-sync' ||
            purpose === 'lighting-sync'
          "
          v-model="selected.axis3"
          class="checkbox"
        >
          {{ $t("selectVersion.inSwitch") }} 3
        </md-checkbox>
        <md-checkbox
          v-if="purpose === 'export'"
          v-model="selected.keyLayer"
          class="checkbox"
        >
          {{ $t("selectVersion.layout") }}
        </md-checkbox>
      </div>
      <div
        v-if="purpose === 'sync' || purpose === 'lighting-sync'"
        class="selection-warning"
        aria-live="polite"
      >
        <span v-if="isPartialLedSelection">
          {{ $t("cyberboardR6.partialLedApplyWarning") }}
        </span>
      </div>
      <div class="buttons">
        <md-button class="button" @click="close">
          {{ $t("cancel") }}
        </md-button>
        <md-button class="button" :disabled="!hasSelection" @click="confirm">
          {{ $t("yes") }}
        </md-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "CyberboardR6ConfigSelection",
  props: {
    value: { type: Boolean, default: false },
    purpose: { type: String, default: "export" },
    currentPagePosition: { type: Number, default: null },
  },
  watch: {
    currentPagePosition: {
      immediate: true,
      handler(position) {
        const keys =
          this.purpose === "sync"
            ? ["led1", "led2", "led3"]
            : this.purpose === "axis-sync"
            ? ["axis1", "axis2", "axis3"]
            : [];
        if (
          !Number.isInteger(position) ||
          position < 0 ||
          position >= keys.length
        ) {
          return;
        }
        keys.forEach((key, index) => {
          this.selected[key] = index === position;
        });
      },
    },
  },
  computed: {
    ...mapState("app", { isCN: "isCN" }),
    title() {
      return this.$t(
        this.purpose === "lighting-sync"
          ? "cyberboardR6.selectToDevice"
          : this.purpose === "sync" || this.purpose === "axis-sync"
          ? "cyberboardR6.selectToSync"
          : "cyberboardR6.selectToExport"
      );
    },
    hasSelection() {
      const keys =
        this.purpose === "sync"
          ? ["led1", "led2", "led3"]
          : this.purpose === "axis-sync"
          ? ["axis1", "axis2", "axis3"]
          : this.purpose === "lighting-sync"
          ? ["led1", "led2", "led3", "axis1", "axis2", "axis3"]
          : Object.keys(this.selected);
      return keys.some((key) => this.selected[key]);
    },
    isPartialLedSelection() {
      return (
        ["sync", "lighting-sync"].includes(this.purpose) &&
        !(this.selected.led1 && this.selected.led2 && this.selected.led3)
      );
    },
  },
  data() {
    return {
      selected: {
        led1: true,
        led2: true,
        led3: true,
        axis1: true,
        axis2: true,
        axis3: true,
        keyLayer: true,
      },
    };
  },
  methods: {
    close() {
      this.$emit("input", false);
    },
    confirm() {
      if (!this.hasSelection) return;
      this.$emit("confirm", {
        led:
          this.purpose === "axis-sync"
            ? [false, false, false]
            : [this.selected.led1, this.selected.led2, this.selected.led3],
        axis:
          this.purpose === "sync"
            ? [false, false, false]
            : [this.selected.axis1, this.selected.axis2, this.selected.axis3],
        keyLayer: this.purpose === "export" ? this.selected.keyLayer : false,
      });
      this.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.c-select {
  @include flex-center;
  width: 900px;
  max-width: 100%;
  flex-direction: column;
  margin: 0 auto;
  position: relative;

  .title {
    margin-bottom: 30px;
    color: #ffffff;
    font-size: 24px;
  }

  .checkbox {
    width: 230px;
    margin: 0 auto 30px;
    color: #ffffff;
    font-size: 20px;
  }

  .buttons {
    @include flex-center;
    width: 100%;
    margin-top: 10px;

    .button {
      width: 150px;
      height: 53px;
      margin: 0;
      border-radius: 5px;
      background-color: $active-color;
      font-size: 20px;
      text-transform: none;

      & + .button {
        margin-left: 100px;
      }
    }
  }

  .selection-warning {
    width: 100%;
    height: 40px;
    margin: 0 0 10px;
    box-sizing: border-box;
    color: #ff4d4f;
    font-size: 16px;
    line-height: 22px;
    text-align: center;
  }

  &.lighting-sync {
    .options {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
    }

    .checkbox {
      margin: 0 auto 20px;
    }
  }

  &.cn .checkbox {
    width: 160px;
  }
}
</style>
